import { PopoverPlacement } from "@idui/react-popover";
import { useCallback, useState, type FC, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";

import {
  DrawerType,
  useDrawers,
  useIsMobile,
  type IFormAttributes,
} from "@ryde/shared";

import { FramerTooltip } from "../../../common";
import {
  ExplanationButtonStyled,
  ExplanationIconStyled,
  ExplanationIconWrapper,
  ExplanationTooltipContent,
} from "./explanation-icon.styled";

interface IFieldExplanationIconProps {
  formAttributes?: IFormAttributes | null;
  horizontalOffset?: number;
}

export const FieldExplanationIcon: FC<IFieldExplanationIconProps> = ({
  formAttributes,
  horizontalOffset,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useIsMobile();
  const { openDrawer } = useDrawers();
  // Controlled so keyboard focus can open the tooltip alongside the library's own hover handling.
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const { tooltipTranslationKey, tooltipTranslationParams, helpTopikUrl } =
    formAttributes || {};

  const hasHelpLink = Boolean(helpTopikUrl);
  const tooltipText = tooltipTranslationKey
    ? t(tooltipTranslationKey, tooltipTranslationParams)
    : undefined;

  const handleHelpClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>): void => {
      if (!helpTopikUrl) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      openDrawer({
        drawerProps: {
          type: DrawerType.HelpTopic,
          helpTopikUrl,
        },
      });
    },
    [helpTopikUrl, openDrawer],
  );

  if (isMobile || (!tooltipText && !hasHelpLink)) {
    return null;
  }

  // A native button is reachable with Tab and opens the help topic on Enter/Space.
  const button = (
    <ExplanationButtonStyled
      type="button"
      $hasHelpLink={hasHelpLink}
      onClick={hasHelpLink ? handleHelpClick : undefined}
      onFocus={() => setIsTooltipOpen(true)}
      onBlur={() => setIsTooltipOpen(false)}
      aria-label={hasHelpLink ? t("helpTopics.openTopic") : tooltipText}
    >
      <ExplanationIconStyled aria-hidden />
    </ExplanationButtonStyled>
  );

  if (!tooltipText) {
    return (
      <ExplanationIconWrapper $horizontalOffset={horizontalOffset}>
        {button}
      </ExplanationIconWrapper>
    );
  }

  return (
    <ExplanationIconWrapper $horizontalOffset={horizontalOffset}>
      <FramerTooltip
        content={
          <ExplanationTooltipContent>{tooltipText}</ExplanationTooltipContent>
        }
        placement={PopoverPlacement.top}
        isOpen={isTooltipOpen}
        onChangeOpen={setIsTooltipOpen}
      >
        {button}
      </FramerTooltip>
    </ExplanationIconWrapper>
  );
};
